import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Loader2, UserRound } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { sendChatMessage, CHAT_SUGGESTIONS } from "../services/chat";
import { trackEvent, EVENTS } from "../services/analytics";

const WELCOME = {
  role: "assistant",
  text: "Hello — I'm the ZasyaOnline assistant. I can answer questions about our products (MoveOS, myBantu, LearnSphere, CraneSoftwares), our consulting services, and how to reach the team.",
};

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [failed, setFailed] = useState(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, typing, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || typing) return;
    setInput("");
    setFailed(null);
    setMessages((m) => [...m, { role: "user", text: message }]);
    setTyping(true);
    try {
      const res = await sendChatMessage({ conversationId: "preview", message });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: res.reply, suggestions: res.suggestions, handoff: res.handoff },
      ]);
      if (res.handoff) trackEvent(EVENTS.CHATBOT_HANDOFF, {});
    } catch {
      setFailed(message);
    } finally {
      setTyping(false);
    }
  };

  const suggestionAction = (s) => {
    if (/demo|contact|talk|team|enquiry/i.test(s)) {
      navigate("/contact");
      setOpen(false);
    } else if (/products/i.test(s)) {
      navigate("/products");
      setOpen(false);
    } else if (/consulting/i.test(s)) {
      navigate("/consulting");
      setOpen(false);
    } else if (/customers/i.test(s)) {
      navigate("/customers");
      setOpen(false);
    } else if (/mybantu website/i.test(s)) {
      window.location.href = "https://www.mybantu.in/";
    } else if (/learnsphere/i.test(s)) {
      navigate("/products/learnsphere");
      setOpen(false);
    } else if (/cranesoftwares/i.test(s)) {
      navigate("/products/cranesoftwares");
      setOpen(false);
    } else if (/about/i.test(s)) {
      navigate("/about");
      setOpen(false);
    } else {
      send(s);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) trackEvent(EVENTS.CHATBOT_START, {});
        }}
        data-testid="chatbot-open-button"
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white shadow-xl shadow-slate-900/25 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-blue-700"
      >
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label="ZasyaOnline assistant"
            className="fixed bottom-[5.5rem] right-5 z-[60] flex h-[32rem] max-h-[70vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            data-testid="chatbot-panel"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-900 px-4 py-3">
              <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-slate-700">
                <img src="/assets/logo-mark.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">ZasyaOnline Assistant</p>
                <p className="text-[11px] text-slate-400">Answers from approved company content</p>
              </div>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    className={
                      m.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 px-3.5 py-2.5 text-sm text-white"
                        : "max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2.5 text-sm text-slate-800"
                    }
                  >
                    <p className="leading-relaxed">{m.text}</p>
                    {m.suggestions?.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {m.suggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => suggestionAction(s)}
                            className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[11px] font-medium text-blue-700 transition-colors hover:bg-blue-50"
                            data-testid={`chatbot-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.handoff && (
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/contact");
                          setOpen(false);
                        }}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white"
                        data-testid="chatbot-handoff-button"
                      >
                        <UserRound size={12} /> Continue with the team
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start" data-testid="chatbot-typing-indicator">
                  <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2.5 text-sm text-slate-500">
                    <Loader2 size={14} className="animate-spin" /> Typing…
                  </div>
                </div>
              )}
              {failed && (
                <div className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800" data-testid="chatbot-error-state">
                  That message didn't go through.{" "}
                  <button type="button" className="font-semibold underline" onClick={() => send(failed)} data-testid="chatbot-retry-button">
                    Retry
                  </button>
                </div>
              )}
            </div>

            {messages.length === 1 && (
              <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-4 py-2.5">
                {CHAT_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-blue-300 hover:text-blue-700"
                    data-testid={`chatbot-starter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              className="flex items-center gap-2 border-t border-slate-100 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about products or services…"
                aria-label="Message the assistant"
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:border-blue-600"
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                aria-label="Send message"
                disabled={!input.trim() || typing}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
                data-testid="chatbot-send-button"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
